/**
 * The activity log: what happened, when, and who did it.
 *
 * Four tables read as one timeline - deliberate actions from `audit.event`,
 * sign-ins from `audit.user_session`, rejected tokens from
 * `audit.auth_failure`, plots, exports and page visits from
 * `audit.usage_event`. They are joined on the way out rather than written
 * twice, so nothing has to be kept in step.
 *
 * **Gated by permission, not by the `/admin` code.** That code is compiled into
 * this bundle and checked in the browser, which is an honest gate for a
 * per-browser display preference and no gate at all for a record of what people
 * did. This asks for `audit:read`, and the server asks again.
 *
 * Refusals are shown, not hidden: "did anyone try?" is usually the question
 * behind the question, and a log that only records what succeeded cannot answer
 * it.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { listAuditActions, listAuditEvents } from '../../api/endpoints.ts';
import type { AuditEvent } from '../../api/types.ts';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { canDo, catalogAvailable, isRootUser, myPermissions } from '../../state/store.ts';
import { userMessage } from '../../api/errors.ts';
import { shipLabel } from '../../utils/ships.ts';
import { openRequestInExplore, pageLabel, signalsOf } from './usage-plot.ts';
import './activity-stats.ts';

const PAGE_SIZE = 100;
const OUTCOMES = { success: 'Succeeded', denied: 'Refused', error: 'Error' };

/** Windows offered by the date filter, as hours back from now. */
const WINDOWS: Array<{ label: string; hours: number | null }> = [
  { label: 'Last 24 hours', hours: 24 },
  { label: 'Last 7 days', hours: 24 * 7 },
  { label: 'Last 30 days', hours: 24 * 30 },
  { label: 'All time', hours: null },
];

/** `chart.share` → "shared", so a line reads as a sentence rather than a key. */
const VERBS: Record<string, string> = {
  'chart.create': 'created chart',
  'chart.update': 'edited chart',
  'chart.delete': 'deleted chart',
  'chart.share': 'shared chart',
  'chart.revoke': 'withdrew access to chart',
  'dashboard.share': 'shared dashboard',
  'dashboard.revoke': 'withdrew access to dashboard',
  'chart.publish': 'made public',
  'chart.unpublish': 'took out of Public',
  'share_link.create': 'made a share link',
  'permission.denied': 'was refused',
  'root.claim': 'became root',
  'session.start': 'signed in',
  'auth.failure': 'was rejected at sign-in',
  'user.role.update': 'changed user role',
  'user.base_role.update': 'changed Explorer role',
  'group.role.update': 'changed group role',
  'access.default.update': 'changed the new-user role',
  'access.migrate': 'moved to predefined roles',
  'user.root.update': 'changed Root rights',
  'user.status.update': 'changed account status',
  'assistant.transcript.read': "read someone's assistant chat",
  'plot.run': 'plotted',
  'export.run': 'exported',
  'page.visit': 'opened page',
};

function describe(event: AuditEvent): string {
  return VERBS[event.action] ?? event.action;
}

/** The one fact about an event worth showing without opening it. */
function summarise(event: AuditEvent): string {
  const d = event.detail ?? {};
  switch (event.action) {
    case 'user.role.update':
      return `${d.role ?? 'role'} · ${d.enabled ? 'granted' : 'removed'}`;
    case 'user.base_role.update':
    case 'access.default.update':
      return `${d.previous ?? 'none'} → ${d.role ?? 'none'}`;
    case 'group.role.update':
      return `${d.role ?? 'role'} · ${d.enabled ? 'assigned' : 'removed'}`;
    case 'user.root.update':
      return d.enabled ? 'granted' : 'removed';
    case 'user.status.update':
      return d.enabled ? 'reactivated' : 'deactivated';
    case 'chart.share':
      return [d.principal, d.role].filter(Boolean).join(' · ') || '';
    case 'chart.revoke':
    case 'dashboard.share':
    case 'dashboard.revoke':
      return [d.principal, d.role].filter(Boolean).join(' · ') || '';
    case 'chart.update':
      return Array.isArray(d.fields) ? (d.fields as string[]).join(', ') : '';
    case 'permission.denied':
      return String(d.permission ?? '');
    case 'assistant.transcript.read':
      return [d.owner_name ? `${d.owner_name}'s chat` : '', d.deleted ? 'deleted' : ''].filter(Boolean).join(' · ');
    case 'auth.failure':
      return String(d.reason ?? '');
    case 'plot.run':
    case 'export.run':
      return [d.ship_id, d.signals != null ? `${d.signals} ${d.signals === 1 ? 'signal' : 'signals'}` : '', d.duration_ms != null ? `${d.duration_ms} ms` : '']
        .filter(Boolean)
        .join(' · ');
    default:
      return '';
  }
}

@customElement('activity-log')
export class ActivityLog extends LightElement {
  private store = new StoreController(this, [myPermissions, isRootUser, catalogAvailable]);

  @state() private events: AuditEvent[] = [];
  @state() private actions: string[] = [];
  @state() private loading = false;
  @state() private error: string | null = null;
  @state() private page = 0;
  @state() private search = '';
  @state() private action = '';
  @state() private outcome = '';
  @state() private windowHours: number | null = 24 * 7;
  /** Which row is expanded to show its raw `detail`. */
  @state() private openId: string | null = null;
  @state() private view: 'log' | 'stats' = 'log';

  private searchTimer: number | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    void this.load();
    void listAuditActions()
      .then((actions) => {
        this.actions = actions;
      })
      .catch(() => {
        // The filter falls back to free text; not worth an error for.
      });
  }

  private since(): string | undefined {
    if (this.windowHours === null) return undefined;
    return new Date(Date.now() - this.windowHours * 3600_000).toISOString();
  }

  private async load(): Promise<void> {
    if (!canDo('audit:read')) return;
    this.loading = true;
    this.error = null;
    const since = this.since();
    try {
      this.events = await listAuditEvents({
        limit: PAGE_SIZE,
        offset: this.page * PAGE_SIZE,
        ...(this.search ? { q: this.search } : {}),
        ...(this.action ? { action: this.action } : {}),
        ...(this.outcome ? { outcome: this.outcome as 'success' | 'denied' | 'error' } : {}),
        ...(since ? { since } : {}),
      });
    } catch (e) {
      this.error = userMessage(e);
    } finally {
      this.loading = false;
    }
  }

  private onSearch(value: string): void {
    this.search = value;
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    // Debounced: this searches a table that only grows.
    this.searchTimer = window.setTimeout(() => {
      this.page = 0;
      void this.load();
    }, 300);
  }

  private renderFilters(): TemplateResult {
    return html`
      <div class="activity-toolbar">
        <div class="activity-search-row">
          <input
            class="catalog-search"
            type="search"
            aria-label="Search activity"
            placeholder="Search people, charts, actions…"
            .value=${this.search}
            @input=${(e: Event) => this.onSearch((e.target as HTMLInputElement).value)}
          />
          <button class="btn btn-secondary" ?disabled=${this.loading} @click=${() => void this.load()}>
            ${this.loading ? html`<span class="spinner" aria-hidden="true"></span>` : nothing}
            Refresh
          </button>
        </div>
        <div class="activity-filter-grid">
          <label>
            Action
            <select
              aria-label="Action"
              .value=${this.action}
              @change=${(e: Event) => {
                this.action = (e.target as HTMLSelectElement).value;
                this.page = 0;
                void this.load();
              }}
            >
              <option value="">Every action</option>
              ${this.actions.map((a) => html`<option value=${a}>${VERBS[a] ?? a}</option>`)}
            </select>
          </label>
          <label>
            Outcome
            <select
              aria-label="Outcome"
              .value=${this.outcome}
              @change=${(e: Event) => {
                this.outcome = (e.target as HTMLSelectElement).value;
                this.page = 0;
                void this.load();
              }}
            >
              <option value="">Any outcome</option>
              ${Object.entries(OUTCOMES).map(([value, label]) => html`<option value=${value}>${label}</option>`)}
            </select>
          </label>
          <label>
            Time range
            <select
              aria-label="Time window"
              @change=${(e: Event) => {
                const hours = (e.target as HTMLSelectElement).value;
                this.windowHours = hours === '' ? null : Number(hours);
                this.page = 0;
                void this.load();
              }}
            >
              ${WINDOWS.map((w) => html`<option value=${w.hours ?? ''} .selected=${w.hours === this.windowHours}>${w.label}</option>`)}
            </select>
          </label>
          <button
            class="catalog-btn"
            ?disabled=${!this.search && !this.action && !this.outcome && this.windowHours === 24 * 7}
            @click=${() => {
              if (this.searchTimer) window.clearTimeout(this.searchTimer);
              this.search = '';
              this.action = '';
              this.outcome = '';
              this.windowHours = 24 * 7;
              this.page = 0;
              void this.load();
            }}
          >Reset filters</button>
        </div>
      </div>
    `;
  }

  private renderRow(event: AuditEvent): TemplateResult {
    const when = new Date(event.occurred_at);
    const open = this.openId === event.id;
    const summary = summarise(event);
    const toggle = (): void => { this.openId = open ? null : event.id; };
    return html`
      <tr class="activity-row ${open ? 'is-open' : ''}" @click=${toggle}>
        <td class="activity-when">
          <time datetime=${event.occurred_at} title=${when.toISOString()}>
            <span>${when.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            <span class="people-muted">${when.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
          </time>
        </td>
        <td class="activity-actor">
          <span title=${event.actor_name ?? event.actor_email ?? 'Unknown person'}>${event.actor_name ?? event.actor_email ?? 'Unknown person'}</span>
        </td>
        <td class="activity-what">
          <button
            class="people-name activity-toggle"
            aria-expanded=${String(open)}
            aria-controls=${open ? `activity-detail-${event.id}` : nothing}
            aria-label=${`${describe(event)}${event.object_name ? ' · ' + event.object_name : ''} — recorded details`}
            @click=${(e: Event) => { e.stopPropagation(); toggle(); }}
          >
            <svg class="activity-chevron" viewBox="0 0 16 16" aria-hidden="true"><path d="m6 4 4 4-4 4" /></svg>
            <span>${describe(event)}</span>
          </button>
          ${event.object_name ? html`<span class="activity-object">${event.object_name}</span>` : nothing}
          ${summary ? html`<span class="activity-summary">${summary}</span>` : nothing}
        </td>
        <td><span class="activity-outcome is-${event.outcome}">${OUTCOMES[event.outcome]}</span></td>
      </tr>
      ${open
        ? html`<tr class="activity-detail-row" id=${`activity-detail-${event.id}`}>
            <td colspan="4">
              <div class="activity-detail-head">
                <h2>Recorded details</h2>
                <code>${event.action}</code>
              </div>
              <dl class="activity-facts">
                <div><dt>Person</dt><dd>${event.actor_email ?? event.actor_name ?? 'Unknown person'}</dd></div>
                <div><dt>Time</dt><dd>${when.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'long' })}</dd></div>
                ${event.object_name ? html`<div><dt>Object</dt><dd>${event.object_name}</dd></div>` : nothing}
              </dl>
              ${event.action === 'plot.run' || event.action === 'export.run'
                ? this.renderPlotDetail(event)
                : html`<pre class="activity-detail">${JSON.stringify({ ...event.detail, ip: event.ip, id: event.id }, null, 2)}</pre>`}
            </td>
          </tr>`
        : nothing}
    `;
  }

  /** A plot or export as facts a person reads, and the way to see it again. */
  private renderPlotDetail(event: AuditEvent): TemplateResult {
    const d = event.detail ?? {};
    const req = (d.request ?? {}) as Record<string, unknown>;
    const signals = signalsOf(req);
    const fact = (label: string, value: unknown) =>
      value == null || value === '' ? nothing : html`<div><dt>${label}</dt><dd>${value}</dd></div>`;
    return html`<dl class="activity-facts">
        ${fact('Ship', d.ship_id ? shipLabel(String(d.ship_id)) : '')}
        ${fact('Data', d.source === 'live' ? 'Live' : d.source ? 'EOS' : '')}
        ${fact('From', d.start ? `${String(d.start).slice(0, 16).replace('T', ' ')} UTC` : '')}
        ${fact('To', d.end ? `${String(d.end).slice(0, 16).replace('T', ' ')} UTC` : '')}
        ${fact('Signals', signals.join(', '))}
        ${fact('Rows', d.rows != null ? Number(d.rows).toLocaleString('en-GB') : '')}
        ${fact('Time taken', d.duration_ms != null ? `${d.duration_ms} ms` : '')}
        ${fact('Page', pageLabel(d.page as string | undefined))}
        ${fact('Error', d.error)}
      </dl>
      ${event.action === 'plot.run' && event.object_name && d.ship_id
        ? html`<button type="button" class="btn btn-secondary" @click=${() => openRequestInExplore(req)}>Open in Explore ↗</button>`
        : nothing}`;
  }

  override render(): TemplateResult {
    if (!catalogAvailable.value) {
      return html`<div class="catalog-empty">
        <p class="catalog-empty-title">The activity log needs the metadata database</p>
        <p>This deployment is running without one, so nothing is being recorded.</p>
      </div>`;
    }
    if (!canDo('audit:read')) {
      return html`<div class="catalog-empty">
        <p class="catalog-empty-title">You do not have access to the activity log</p>
        <p>
          It records what everyone in the app has done, so it is limited to administrators. Ask
          one of them, or the root account, for <code>audit:read</code>.
        </p>
      </div>`;
    }
    const tabs = html`<div class="transcripts-bar">
      <div class="seg" role="group" aria-label="View">
        <button type="button" class="seg-btn ${this.view === 'log' ? 'active' : ''}"
          aria-pressed=${this.view === 'log'} @click=${() => { this.view = 'log'; }}>Log</button>
        <button type="button" class="seg-btn ${this.view === 'stats' ? 'active' : ''}"
          aria-pressed=${this.view === 'stats'} @click=${() => { this.view = 'stats'; }}>Statistics</button>
      </div>
    </div>`;
    if (this.view === 'stats') return html`${tabs}<activity-stats></activity-stats>`;
    return html`${tabs}
      <section class="activity">
        ${this.renderFilters()}
        <div class="activity-feed-head">
          <span role="status">${this.events.length} ${this.events.length === 1 ? 'event' : 'events'} on this page</span>
          <span>Newest first · Times are local</span>
        </div>
        <div class="activity-results" aria-busy=${String(this.loading)}>
        ${this.error
          ? html`<div class="catalog-empty">
              <p class="catalog-empty-title">The log could not be read</p>
              <p>${this.error}</p>
            </div>`
          : this.events.length
            ? html`<div class="people-table-wrap activity-list">
                <table class="people-table activity-table" aria-label="Activity log">
                  <thead><tr><th scope="col">Time</th><th scope="col">Person</th><th scope="col">Event</th><th scope="col">Outcome</th></tr></thead>
                  <tbody>${this.events.map((e) => this.renderRow(e))}</tbody>
                </table>
              </div>`
            : html`<div class="catalog-empty">
                <p class="catalog-empty-title">
                  ${this.loading
                    ? html`<span class="spinner" role="status" aria-label="Loading activity"></span>`
                    : this.search || this.action || this.outcome
                      ? 'No events match your filters'
                      : 'Nothing recorded in this time range'}
                </p>
                ${this.loading
                  ? nothing
                  : html`<p>Widen the time range, or clear the filters.</p>`}
              </div>`}
        </div>
        ${this.events.length >= PAGE_SIZE || this.page > 0
          ? html`<div class="activity-pager">
              <button
                class="catalog-btn"
                ?disabled=${this.page === 0 || this.loading}
                @click=${() => {
                  this.page -= 1;
                  void this.load();
                }}
              >
                Newer
              </button>
              <span>Page ${this.page + 1}</span>
              <button
                class="catalog-btn"
                ?disabled=${this.events.length < PAGE_SIZE || this.loading}
                @click=${() => {
                  this.page += 1;
                  void this.load();
                }}
              >
                Older
              </button>
            </div>`
          : nothing}
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'activity-log': ActivityLog;
  }
}
